import { Box, Flex, Grid, Heading, Stack, Text } from '@chakra-ui/react';
import { useLingui } from '@lingui/react/macro';
import * as m from 'motion/react-m';

// --- Mode icons (the three ways to squad up) ---
const ListSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="8" y1="6" x2="21" y2="6" />
    <line x1="8" y1="12" x2="21" y2="12" />
    <line x1="8" y1="18" x2="21" y2="18" />
    <circle cx="3.5" cy="6" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="12" r="1.2" fill="currentColor" stroke="none" />
    <circle cx="3.5" cy="18" r="1.2" fill="currentColor" stroke="none" />
  </svg>
);
const ZapSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <polygon points="13 2 3 14 12 14 11 22 21 10 12 10 13 2" />
  </svg>
);
const CalendarSvg = () => (
  <svg
    width="24"
    height="24"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <rect width="18" height="18" x="3" y="4" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" />
    <line x1="8" y1="2" x2="8" y2="6" />
    <line x1="3" y1="10" x2="21" y2="10" />
    <circle cx="12" cy="16" r="1.1" fill="currentColor" stroke="none" />
  </svg>
);

// --- Tool icons (smaller - the tools that power every post) ---
const HeadphonesSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M3 18v-6a9 9 0 0 1 18 0v6" />
    <path d="M21 19a2 2 0 0 1-2 2h-1a2 2 0 0 1-2-2v-3a2 2 0 0 1 2-2h3zM3 19a2 2 0 0 0 2 2h1a2 2 0 0 0 2-2v-3a2 2 0 0 0-2-2H3z" />
  </svg>
);
const SearchSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="11" cy="11" r="8" />
    <path d="m21 21-4.3-4.3" />
    <line x1="11" y1="8" x2="11" y2="14" />
    <line x1="8" y1="11" x2="14" y2="11" />
  </svg>
);
const ShareSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <circle cx="18" cy="5" r="3" />
    <circle cx="6" cy="12" r="3" />
    <circle cx="18" cy="19" r="3" />
    <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
    <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
  </svg>
);
const MessageSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M7.9 20A9 9 0 1 0 4 16.1L2 22Z" />
  </svg>
);
// Comments (public, under a post) - a speech bubble with text lines, distinct from the
// round DM bubble above so the two communication features don't read as the same thing.
const CommentSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M21 15a2 2 0 0 1-2 2H8l-4 4V5a2 2 0 0 1 2-2h13a2 2 0 0 1 2 2z" />
    <line x1="8" y1="9" x2="16" y2="9" />
    <line x1="8" y1="13" x2="13" y2="13" />
  </svg>
);
const UserSvg = () => (
  <svg
    width="20"
    height="20"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" />
    <circle cx="12" cy="7" r="4" />
  </svg>
);

type Tone = 'neutral' | 'live' | 'accent';

const toneColor: Record<Tone, string> = {
  neutral: 'rgba(255,255,255,0.55)',
  live: '#22c55e',
  accent: '#8E7BFF',
};

const StatusBadge = ({ tone, label }: { tone: Tone; label: string }) => (
  <Flex
    alignItems="center"
    gap={1.5}
    px={2.5}
    py={1}
    borderRadius="full"
    bg="whiteAlpha.100"
    border="1px solid"
    borderColor="whiteAlpha.100"
    flexShrink={0}
  >
    {tone === 'live' ? (
      <Box
        w="7px"
        h="7px"
        borderRadius="full"
        bg={toneColor.live}
        flexShrink={0}
        style={{ boxShadow: `0 0 8px ${toneColor.live}` }}
        asChild
      >
        <m.div
          animate={{ opacity: [1, 0.4, 1] }}
          transition={{ duration: 1.4, repeat: Infinity, ease: 'easeInOut' }}
        />
      </Box>
    ) : (
      <Box
        w="6px"
        h="6px"
        borderRadius="full"
        flexShrink={0}
        style={{ background: toneColor[tone] }}
      />
    )}
    <Text
      fontSize="2xs"
      fontWeight="800"
      textTransform="uppercase"
      letterSpacing="0.08em"
      whiteSpace="nowrap"
      style={{ color: toneColor[tone] }}
    >
      {label}
    </Text>
  </Flex>
);

type Mode = {
  icon: React.ReactNode;
  tone: Tone;
  badge: string;
  title: string;
  description: string;
};

const ModeCard = ({ mode, delay }: { mode: Mode; delay: number }) => (
  <Flex
    direction="column"
    gap={5}
    p={{ base: 5, md: 6 }}
    borderRadius="2xl"
    background="linear-gradient(165deg, hsla(228, 11.6%, 14%, 0.85) 0%, hsla(228, 11.6%, 10%, 0.45) 100%)"
    border="1px solid"
    borderColor="whiteAlpha.100"
    transition="all 0.3s ease-out"
    _hover={{ borderColor: 'purple.400', transform: 'translateY(-3px)' }}
    asChild
  >
    <m.div
      initial={{ opacity: 0, y: 24 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.45 }}
    >
      <Flex alignItems="center" justifyContent="space-between">
        <Flex
          w="48px"
          h="48px"
          borderRadius="xl"
          alignItems="center"
          justifyContent="center"
          flexShrink={0}
          color="purple.300"
          bg="whiteAlpha.50"
          border="1px solid"
          borderColor="whiteAlpha.100"
        >
          {mode.icon}
        </Flex>
        <StatusBadge tone={mode.tone} label={mode.badge} />
      </Flex>
      <Box>
        <Text fontWeight="800" fontSize={{ base: 'lg', md: 'xl' }} lineHeight="short" mb={2}>
          {mode.title}
        </Text>
        <Text fontSize="sm" color="fg.muted" lineHeight="1.7">
          {mode.description}
        </Text>
      </Box>
    </m.div>
  </Flex>
);

type Feature = { icon: React.ReactNode; title: string; desc: string };

/**
 * One capability as a standalone cell: icon on top, name, then a one-line benefit
 * (the "why"). Six of these sit in a symmetric 3×2 grid - the flat "everything's
 * already inside" inventory, deliberately NOT a journey (that story lives in
 * HowItWorks). Vertical layout gives each feature more room to read as substance.
 */
// Responsive by design: on mobile it's a compact row (icon left, text right, tight
// padding) so six features don't turn into a wall of tall cards; from md up it becomes
// the vertical card used in the 3×2 desktop grid.
const FeatureCell = ({ feature, delay }: { feature: Feature; delay: number }) => (
  <Flex
    direction={{ base: 'row', md: 'column' }}
    gap={{ base: 3.5, md: 3.5 }}
    p={{ base: 4, md: 6 }}
    borderRadius={{ base: 'xl', md: '2xl' }}
    background="linear-gradient(165deg, hsla(228, 11.6%, 13%, 0.85) 0%, hsla(228, 11.6%, 9%, 0.5) 100%)"
    border="1px solid"
    borderColor="whiteAlpha.100"
    transition="all 0.3s ease-out"
    _hover={{ borderColor: 'purple.400', transform: { base: 'none', md: 'translateY(-3px)' } }}
    asChild
  >
    <m.div
      initial={{ opacity: 0, y: 22 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ delay, duration: 0.45 }}
    >
      <Flex
        w={{ base: '40px', md: '44px' }}
        h={{ base: '40px', md: '44px' }}
        borderRadius="xl"
        alignItems="center"
        justifyContent="center"
        flexShrink={0}
        color="purple.300"
        bg="whiteAlpha.50"
        border="1px solid"
        borderColor="whiteAlpha.100"
      >
        {feature.icon}
      </Flex>
      <Box>
        <Text
          fontSize={{ base: 'sm', md: 'md' }}
          fontWeight="700"
          color="fg.emphasized"
          lineHeight="1.3"
          mb={{ base: 1, md: 1.5 }}
        >
          {feature.title}
        </Text>
        <Text fontSize={{ base: 'xs', md: 'sm' }} color="fg.muted" lineHeight="1.6">
          {feature.desc}
        </Text>
      </Box>
    </m.div>
  </Flex>
);

/**
 * "Post it your way" - merges the old Showcase (three ways to squad up) with Features
 * (the tools). The three post types are the primary, use-case-framed hook; the tools
 * sit underneath as a lighter supporting row, since every post ships with them. Kills
 * the old "three cards then six cards" duplication (the "Scheduled Sessions" feature is
 * absorbed into the Scheduled mode).
 */
export const PlayYourWay = () => {
  const { t } = useLingui();

  const modes: Mode[] = [
    {
      icon: <ListSvg />,
      tone: 'neutral',
      badge: t`v3.showcase.open.badge`,
      title: t`v3.showcase.open.title`,
      description: t`v3.showcase.open.desc`,
    },
    {
      icon: <ZapSvg />,
      tone: 'live',
      badge: t`v3.showcase.now.badge`,
      title: t`v3.showcase.now.title`,
      description: t`v3.showcase.now.desc`,
    },
    {
      icon: <CalendarSvg />,
      tone: 'accent',
      badge: t`v3.showcase.plan.badge`,
      title: t`v3.showcase.plan.title`,
      description: t`v3.showcase.plan.desc`,
    },
  ];

  // The flat inventory - everything that ships with the product, in a symmetric 3×2 grid.
  // Comments (public, under a post) and direct messages (private, 1-on-1) are split into
  // two distinct features instead of a single vague "built-in chat".
  const features: Feature[] = [
    {
      icon: <HeadphonesSvg />,
      title: t`v3.showcase.feat.voice.title`,
      desc: t`v3.showcase.feat.voice.desc`,
    },
    {
      icon: <SearchSvg />,
      title: t`v3.showcase.feat.search.title`,
      desc: t`v3.showcase.feat.search.desc`,
    },
    {
      icon: <CommentSvg />,
      title: t`v3.showcase.feat.comments.title`,
      desc: t`v3.showcase.feat.comments.desc`,
    },
    {
      icon: <ShareSvg />,
      title: t`v3.showcase.feat.banner.title`,
      desc: t`v3.showcase.feat.banner.desc`,
    },
    {
      icon: <UserSvg />,
      title: t`v3.showcase.feat.profiles.title`,
      desc: t`v3.showcase.feat.profiles.desc`,
    },
    {
      icon: <MessageSvg />,
      title: t`v3.showcase.feat.chat.title`,
      desc: t`v3.showcase.feat.chat.desc`,
    },
  ];

  return (
    <Box as="section" aria-label="Ways to play" w="100%">
      <Stack gap={3} mb={{ base: 8, md: 10 }} textAlign="center" alignItems="center">
        <Text
          fontSize="xs"
          fontWeight="700"
          color="purple.300"
          textTransform="uppercase"
          letterSpacing="0.15em"
        >
          {t`v3.showcase.label`}
        </Text>
        <Heading
          fontWeight="900"
          lineHeight="1.05"
          fontSize={{ base: '3xl', lg: '5xl' }}
          as="h2"
          textTransform="uppercase"
          letterSpacing="-0.02em"
          maxW="640px"
        >
          {t`v3.showcase.title`}
        </Heading>
        <Text fontSize="md" color="fg.muted" maxW="560px">
          {t`v3.showcase.subtitle`}
        </Text>
      </Stack>

      <Grid templateColumns={{ base: '1fr', md: 'repeat(3, 1fr)' }} gap={4}>
        {modes.map((mode, index) => (
          <ModeCard key={mode.title} mode={mode} delay={index * 0.1} />
        ))}
      </Grid>

      {/* The flat "everything's already inside" inventory - a labelled divider, then a
          symmetric 3×2 grid of every capability. Not a journey (HowItWorks owns that). */}
      <Box mt={{ base: 10, md: 14 }}>
        <Flex alignItems="center" gap={4} mb={{ base: 6, md: 8 }}>
          <Box flex="1" h="1px" bg="whiteAlpha.100" />
          <Text
            fontSize="xs"
            fontWeight="700"
            color="purple.300"
            textTransform="uppercase"
            letterSpacing="0.15em"
            whiteSpace="nowrap"
          >
            {t`v3.showcase.feat.label`}
          </Text>
          <Box flex="1" h="1px" bg="whiteAlpha.100" />
        </Flex>
        <Grid
          templateColumns={{ base: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(3, 1fr)' }}
          gap={{ base: 2.5, md: 4 }}
          alignItems="stretch"
        >
          {features.map((feature, index) => (
            <FeatureCell key={feature.title} feature={feature} delay={(index % 3) * 0.08} />
          ))}
        </Grid>
      </Box>
    </Box>
  );
};
